import React, { useMemo, useState } from "react"
import CustomDateRange from "./CustomDateRange"
import dayjs from "dayjs"
import generateTransactionsPDF from "./generateTransactionsPDF"
import Select from "react-select"
import { Button } from "../../../../components/ui/button"
import { SvgDownload } from "../../../../components/icons"
import { useNavigate } from "react-router-dom"

interface Props {
  transactions: any[]
  user: any
}

const ClientTransactions: React.FC<Props> = ({ transactions, user }) => {
  const [visibleCount, setVisibleCount] = useState(10)
  const [selectedOptions, setSelectedOptions] = useState<any[]>([])
  const [startDate, setStartDate] = useState<string>("")
  const [endDate, setEndDate] = useState<string>("")
  const today = dayjs().startOf("day")
  const navigate = useNavigate()

  const computedStatuses = useMemo(() => {
    return transactions.map((t) => {
      const status = t.payment_status?.toLowerCase() || ""
      const date = dayjs(t.date_scheduled || t.date_processed)
      const isOverdue = status === "scheduled" && date.isBefore(today)
      if (isOverdue) return "overdue"
      return status
    })
  }, [transactions, today])

  const uniqueClients = useMemo(
    () =>
      Array.from(
        new Set(transactions.map((t) => t.client_name).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [transactions]
  )

  const uniqueServices = useMemo(
    () =>
      Array.from(
        new Set(transactions.map((t) => t.service_name).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [transactions]
  )

  const uniqueStatuses = useMemo(
    () =>
      Array.from(new Set(computedStatuses))
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [computedStatuses]
  )

  const uniquePromoCodes = useMemo(
    () =>
      Array.from(
        new Set(transactions.map((t) => t.promo_code).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [transactions]
  )

  const filterOptions = useMemo(
    () => [
      {
        label: "CLIENTS",
        options:
          uniqueClients.length > 0
            ? uniqueClients.map((c) => ({
                value: `client_${c}`,
                label: c,
                group: "client",
              }))
            : [{ value: "none", label: "No Clients", isDisabled: true }],
      },
      {
        label: "SERVICES",
        options:
          uniqueServices.length > 0
            ? uniqueServices.map((s) => ({
                value: `service_${s}`,
                label: s,
                group: "service",
              }))
            : [{ value: "none", label: "No Services", isDisabled: true }],
      },
      {
        label: "PROMO CODES",
        options:
          uniquePromoCodes.length > 0
            ? uniquePromoCodes.map((p) => ({
                value: `promo_${p}`,
                label: p,
                group: "promo",
              }))
            : [{ value: "none", label: "No Promo Codes", isDisabled: true }],
      },
      {
        label: "STATUS",
        options:
          uniqueStatuses.length > 0
            ? uniqueStatuses.map((s) => ({
                value: `status_${s}`,
                label: s.charAt(0).toUpperCase() + s.slice(1),
                group: "status",
              }))
            : [{ value: "none", label: "No Statuses", isDisabled: true }],
      },
    ],
    [uniqueClients, uniqueServices, uniqueStatuses]
  )

  const filtered = useMemo(() => {
    let valid = transactions.filter(
      (t) => t.payment_status?.toLowerCase() !== "canceled"
    )

    const selectedClients = selectedOptions
      .filter((opt) => opt.group === "client")
      .map((opt) => opt.label)
    const selectedServices = selectedOptions
      .filter((opt) => opt.group === "service")
      .map((opt) => opt.label)
    const selectedPromoCodes = selectedOptions
      .filter((opt) => opt.group === "promo")
      .map((opt) => opt.label)
    const selectedStatuses = selectedOptions
      .filter((opt) => opt.group === "status")
      .map((opt) => opt.label.toLowerCase())

    if (startDate) {
      valid = valid.filter((t) =>
        dayjs(t.date_processed || t.date_scheduled).isAfter(
          dayjs(startDate).subtract(1, "day")
        )
      )
    }

    if (endDate) {
      valid = valid.filter((t) =>
        dayjs(t.date_processed || t.date_scheduled).isBefore(
          dayjs(endDate).add(1, "day")
        )
      )
    }

    if (selectedClients.length)
      valid = valid.filter((t) => selectedClients.includes(t.client_name))
    if (selectedServices.length)
      valid = valid.filter((t) => selectedServices.includes(t.service_name))
    if (selectedPromoCodes.length)
      valid = valid.filter((t) => selectedPromoCodes.includes(t.promo_code))
    if (selectedStatuses.length) {
      valid = valid.filter((t) => {
        const status = t.payment_status?.toLowerCase() || ""
        const date = dayjs(t.date_scheduled || t.date_processed)
        const isOverdue = status === "scheduled" && date.isBefore(today)
        const uiStatus = isOverdue ? "overdue" : status
        return selectedStatuses.includes(uiStatus)
      })
    }

    return valid
  }, [transactions, selectedOptions, today])

  const totalEarnings = filtered
    .filter((p) => p.payment_status?.toLowerCase() === "paid")
    .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)
    .toFixed(2)

  const handleDownloadAll = () => {
    generateTransactionsPDF("Client Payments", filtered, "client", user)
  }

  const getStatusAndAction = (t: any) => {
    const status = t.payment_status?.toLowerCase() || ""
    const date = dayjs(t.date_scheduled || t.date_processed)
    const isOverdue = status === "scheduled" && date.isBefore(today)

    if (isOverdue)
      return { label: "Overdue", variant: "overdue", action: "message" }
    if (status === "paid")
      return { label: "Paid", variant: "success", action: null }
    if (status === "refunded")
      return { label: "Refunded", variant: "refunded", action: null }
    if (status === "denied")
      return { label: "Denied", variant: "error", action: null }
    if (status === "scheduled")
      return { label: "Scheduled", variant: "neutral", action: null }
    return { label: "Unknown", variant: "neutral", action: null }
  }

  const getBadgeClass = (variant: string) => {
    switch (variant) {
      case "success":
        return "bg-green text-foreground border-transparent"
      case "error":
        return "bg-destructive text-destructive-foreground border-transparent"
      case "overdue":
        return "bg-vividOrange text-white border-transparent"
      case "refunded":
        return "bg-[orange] text-black border-transparent"
      default:
        return "border border-black text-black"
    }
  }

  const startMeetingChat = (profileId: string | number) => {
    if (!profileId) return
    navigate(`/coach/inbox`, {
      state: {
        participantIds: [profileId.toString(), user.activeProfileId.toString()],
      },
    })
  }

  const customStyles = {
    control: (provided: any) => ({
      ...provided,
      minHeight: "42px",
      height: "42px",
      alignItems: "center",
      padding: "0 4px",
    }),
    valueContainer: (provided: any) => ({
      ...provided,
      padding: "0 6px",
      height: "42px",
      display: "flex",
      alignItems: "center",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      margin: "2px 4px",
      backgroundColor: "#eef2ff",
      borderRadius: "6px",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "#333",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      ":hover": { backgroundColor: "#ddd", color: "#000" },
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isFocused
        ? "#E8EFF5"
        : state.isSelected
        ? "#727679"
        : "white",
      color: state.isFocused ? "black" : state.isSelected ? "white" : "#333",
      ":active": {
        backgroundColor: "#727679",
        color: "white",
      },
    }),
  }

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8 mt-6">
        <div className="flex flex-col sm:flex-row md:flex-nowrap gap-3 w-full md:w-auto items-start md:items-end">
          <div className="flex items-center gap-2 text-[26px] font-semibold">
            <span>Earnings to Date: ${totalEarnings}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-[48px] w-[48px] flex items-center justify-center"
              onClick={handleDownloadAll}
            >
              <SvgDownload className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex flex-row flex-wrap items-center md:items-end gap-3 w-full md:w-auto md:ml-auto md:justify-end">
          <div className="flex-1 min-w-[150px] max-w-[230px]">
            <CustomDateRange
              onRangeChange={(start, end) => {
                setStartDate(start)
                setEndDate(end)
              }}
            />
          </div>

          <div className="flex-1 min-w-[120px] max-w-[300px]">
            <Select
              classNamePrefix="select"
              options={filterOptions}
              placeholder="Filter by client, service, or status..."
              isMulti
              isSearchable={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              controlShouldRenderValue
              isClearable
              styles={customStyles}
              value={selectedOptions}
              onChange={(opts: any) => setSelectedOptions(opts || [])}
            />
          </div>
        </div>
      </div>

      <div className="border border-grayMist rounded-lg p-6">
        {/* Desktop (lg and up) */}
        <div className="hidden lg:grid grid-cols-[1fr_2.5fr_1.25fr_1fr_1fr_1fr_1fr] text-[18px] font-semibold border-b pb-2 mb-4">
          <span>Date</span>
          <span>Description</span>
          <span className="text-center">Payment Method</span>
          <span className="text-center">Amount</span>
          <span className="text-center">Promo Code</span>
          <span className="text-center">Status</span>
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-500 text-[16px]">
            No transactions found.
          </div>
        ) : (
          <>
            {[...filtered]
              .sort(
                (a, b) =>
                  dayjs(b.date_processed || b.date_scheduled).valueOf() -
                  dayjs(a.date_processed || a.date_scheduled).valueOf()
              )
              .slice(0, visibleCount)
              .map((p, i) => {
                const { label, variant, action } = getStatusAndAction(p)
                const status = (p.payment_status || "").toLowerCase()
                const method = String(p.payment_method || "").toLowerCase()
                const amount =
                  p.amount !== null && p.amount !== undefined
                    ? parseFloat(p.amount)
                    : null
                const isOverdue =
                  status === "scheduled" &&
                  dayjs(p.date_scheduled).isBefore(today)

                let paymentDisplay = "—"

                if (status === "refunded") {
                  paymentDisplay = "Refunded"
                } else if (amount && amount > 0) {
                  if (method.includes("paypal")) {
                    paymentDisplay = "PayPal"
                  } else if (/\d{4}$/.test(method)) {
                    paymentDisplay = `•••• ${method.slice(-4)}`
                  } else if (method) {
                    paymentDisplay = "Card"
                  }
                }

                return (
                  <div
                    key={i}
                    className="border-b py-3 text-[16px] flex flex-col lg:grid grid-cols-[1fr_2.5fr_1.25fr_1fr_1fr_1fr_1fr] md:items-center gap-1 md:gap-0"
                  >
                    {/* --- Desktop (lg and up) --- */}
                    <div className="hidden lg:contents">
                      <span>
                        {dayjs(p.date_processed || p.date_scheduled).format(
                          "M/D/YYYY"
                        )}
                      </span>
                      <span>
                        <span className="font-medium">
                          {p.client_name || "Unknown Client"}
                        </span>{" "}
                        — {p.service_name || "Service"}
                      </span>
                      <span className="text-center">{paymentDisplay}</span>
                      <span className="text-center">
                        ${parseFloat(p.amount || 0).toFixed(2)}
                      </span>
                      <div className="flex justify-center">
                        {p.promo_code ? (
                          <span className="inline-block max-w-[10ch] break-words whitespace-normal text-center">
                            {p.promo_code}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                      <span
                        className={`font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
                          variant
                        )}`}
                        style={{ minWidth: "110px", height: "32px" }}
                      >
                        {label}
                      </span>
                      <span className="text-center">
                        {action === "message" ? (
                          <Button
                            variant="outlineDynamic"
                            size="sm"
                            style={{ color: "#FF4A00", borderColor: "#FF4A00" }}
                            onClick={() => startMeetingChat(p.profile_id)}
                            disabled={!p.profile_id}
                            className={
                              !p.profile_id
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }
                          >
                            Message
                          </Button>
                        ) : (
                          <div className="min-h-[32px]" />
                        )}
                      </span>
                    </div>

                    {/* --- Medium (md only) --- */}
                    <div className="hidden md:grid lg:hidden grid-cols-[2fr_1fr] items-center gap-4 w-full border-b py-3">
                      <div className="flex flex-col">
                        <span className="text-gray-500 text-sm">
                          {dayjs(p.date_processed || p.date_scheduled).format(
                            "M/D/YYYY"
                          )}
                        </span>
                        <span className="font-medium break-words">
                          {p.client_name || "Unknown Client"} —{" "}
                          {p.service_name || "Service"}
                        </span>
                      </div>

                      <div className="flex flex-col items-end justify-center text-right gap-1">
                        <span
                          className={`font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
                            variant
                          )}`}
                          style={{ minWidth: "110px", height: "32px" }}
                        >
                          {label}
                        </span>

                        {action === "message" && (
                          <Button
                            variant="outlineDynamic"
                            size="sm"
                            style={{ color: "red", borderColor: "red" }}
                            onClick={() => startMeetingChat(p.profile_id)}
                            disabled={!p.profile_id}
                            className={
                              !p.profile_id
                                ? "opacity-50 cursor-not-allowed mt-1"
                                : "mt-1"
                            }
                          >
                            Message
                          </Button>
                        )}
                      </div>

                      <div className="col-span-2 flex justify-between mt-1">
                        <span className="text-gray-700 text-base">
                          ${parseFloat(p.amount || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>

                    {/* --- Mobile (sm and below) --- */}
                    <div className="flex flex-col md:hidden text-grayCharcoal">
                      <div className="font-medium">
                        {dayjs(p.date_processed || p.date_scheduled).format(
                          "M/D/YYYY"
                        )}
                      </div>
                      <div>
                        <span className="font-medium">
                          {p.client_name || "Unknown Client"}
                        </span>{" "}
                        — {p.service_name || "Service"}
                      </div>
                      <div>${parseFloat(p.amount || 0).toFixed(2)}</div>
                      {p.promo_code && <div>Promo: {p.promo_code}</div>}
                      <div className="flex items-center gap-2 mt-2">
                        <span
                          className={`font-semibold capitalize rounded-xl px-3 py-1 inline-flex justify-center items-center ${getBadgeClass(
                            variant
                          )}`}
                          style={{ minWidth: "110px", height: "32px" }}
                        >
                          {label}
                        </span>
                        {action === "message" && (
                          <Button
                            variant="outlineDynamic"
                            size="sm"
                            style={{ color: "red", borderColor: "red" }}
                            onClick={() => startMeetingChat(p.profile_id)}
                            disabled={!p.profile_id}
                            className={
                              !p.profile_id
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            }
                          >
                            Message
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}

            {visibleCount < filtered.length && (
              <div className="flex justify-center mt-6">
                <Button
                  variant="outline"
                  onClick={() => setVisibleCount((v) => v + 10)}
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default ClientTransactions
